import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import RequestService from '../services/requestService';
import ProcessStepper from '../components/common/ProcessStepper';
import DateUtils from '../utils/dateUtils';
import { ChevronLeft as ChevronLeftIcon } from '@mui/icons-material';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import MainLayout from '../layout/MainLayout';


const OrderExecution = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // ID aus verschiedenen Quellen extrahieren
  const idFromParams = params.id;
  const idFromState = location.state?.requestId;
  
  // Extrahiere die ID aus der URL, wenn sie als Query-Parameter vorhanden ist
  const urlParams = new URLSearchParams(location.search);
  const queryId = urlParams.get('id');
  
  // ID für die Komponente festlegen
  let id = idFromParams || idFromState || queryId;
  
  // Fallback auf ID 7 für Testzwecke, wenn keine ID gefunden wurde
  if (!id) {
    id = '7'; // ID 7 ist die Herzinsuffizienz-Therapie mit in_progress Status
    console.log("Keine ID gefunden, verwende Fallback-ID 7 für Testzwecke");
  }
  
  console.log("Verwendete ID:", id, "Typ:", typeof id);
  
  const [activeStep, setActiveStep] = useState(2); // Entspricht dem aktuellen Schritt (0-basiert)
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Laden der Auftragsdaten
  useEffect(() => {
    const loadOrder = async () => {
      try {
        // Bestimme die zu verwendende Request-ID
        let requestId;
        
        // Wenn eine ID verfügbar ist, verwende diese
        if (id) {
          requestId = parseInt(id, 10);
          console.log("ID verfügbar:", requestId, typeof requestId);
          
          // Zusätzliche Prüfung, ob die ID gültig ist
          if (isNaN(requestId)) {
            console.error("ID konnte nicht in eine Zahl umgewandelt werden:", id);
            setLoading(false);
            return;
          }
        } else {
          console.error("Keine gültige ID gefunden");
          setLoading(false);
          return;
        }
        
        console.log("Lade Anfrage mit ID:", requestId);
        const requestData = await RequestService.getRequestById(requestId);
        console.log("Geladene Anfragedaten:", requestData);
          
        if (requestData) {
          // Konvertiere die Anfragedaten in das Format für die Auftragsausführung
          const orderData = {
            id: requestData.id,
            title: requestData.title,
            company: requestData.company,
            date: requestData.date,
            status: requestData.status,
            statusHistory: requestData.statusHistory || {},
            price: requestData.price || 4000,
            patientCount: requestData.patientCount || 100,
            description: requestData.description || 'Keine Beschreibung verfügbar.',
            steps: []
          };
          
          // Wenn die Anfrage bereits Ausführungsdaten hat, verwende diese
          if (requestData.execution && requestData.execution.steps) {
            orderData.steps = requestData.execution.steps;
          } else {
            // Ansonsten erstelle Standardschritte
            orderData.steps = [
              {
                id: 1,
                name: 'Datenextraktion',
                status: 'completed',
                progress: 100,
                startTime: '2025-03-10T09:30:00',
                endTime: '2025-03-10T10:45:00',
                details: 'Extraktion der Patientendaten aus dem klinischen Informationssystem.'
              },
              {
                id: 2,
                name: 'Pseudonymisierung',
                status: 'completed',
                progress: 100,
                startTime: '2025-03-10T10:50:00',
                endTime: '2025-03-10T11:15:00',
                details: 'Pseudonymisierung der Patientenidentifikatoren.'
              },
              {
                id: 3,
                name: 'Datenharmonisierung',
                status: 'in_progress',
                progress: 65,
                startTime: '2025-03-10T11:20:00',
                endTime: null,
                details: 'Harmonisierung der Daten gemäß OMOP-Standard.'
              },
              {
                id: 4,
                name: 'Datenanalyse',
                status: 'pending',
                progress: 0,
                startTime: null,
                endTime: null,
                details: 'Durchführung der angeforderten Analysen.'
              },
              {
                id: 5,
                name: 'Berichterstellung',
                status: 'pending',
                progress: 0,
                startTime: null,
                endTime: null,
                details: 'Erstellung des finalen Berichts mit Ergebnissen.'
              }
            ];
          }
          
          setOrder(orderData);
          setLoading(false);
        } else {
          console.error("Keine Anfrage mit ID", requestId, "gefunden");
          setLoading(false);
        }
      } catch (error) {
        console.error("Fehler beim Laden der Anfrage:", error);
        setLoading(false);
      }
    };
    
    loadOrder();
  }, [id]);

  const handleStartStep = (stepId) => {
    // In einer realen Anwendung würde hier der Schritt gestartet werden
    console.log('Starting step:', stepId);
  };

  const handlePauseStep = (stepId) => {
    // In einer realen Anwendung würde hier der Schritt pausiert werden
    console.log('Pausing step:', stepId);
  };

  const handleRestartStep = (stepId) => {
    // In einer realen Anwendung würde hier der Schritt neu gestartet werden
    console.log('Restarting step:', stepId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Noch nicht gestartet';
    const options = { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit', 
      hour: '2-digit', 
      minute: '2-digit' 
    };
    return new Date(dateString).toLocaleDateString('de-DE', options);
  };

  const getStepIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'in_progress':
        return <PendingIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'pending':
      default:
        return <PendingIcon color="disabled" />;
    }
  };

  const getStepActions = (step) => {
    switch (step.status) {
      case 'completed':
        return (
          <Tooltip title="Schritt neu starten">
            <IconButton size="small" onClick={() => handleRestartStep(step.id)}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        );
      case 'in_progress':
        return (
          <Tooltip title="Schritt pausieren">
            <IconButton size="small" onClick={() => handlePauseStep(step.id)}>
              <PauseIcon />
            </IconButton>
          </Tooltip>
        );
      case 'pending':
        return (
          <Tooltip title="Schritt starten">
            <IconButton 
              size="small" 
              onClick={() => handleStartStep(step.id)}
              disabled={activeStep !== order.steps.findIndex(s => s.id === step.id) - 1}
            >
              <PlayArrowIcon />
            </IconButton>
          </Tooltip>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (!order) {
    return (
      <MainLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Der angeforderte Auftrag konnte nicht gefunden werden.
          </Alert>
          <Button variant="contained" onClick={() => navigate('/incoming-requests')}>
            Zurück zur Übersicht
          </Button>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Paper elevation={2} sx={{ p: 0, pt: 0, mb: 4, borderRadius: 2, overflow: 'hidden' }}>
          <ProcessStepper 
            activeStep={3} 
            title="Bearbeitungsstatus der Anfrage" 
            requestStatus={order.status} 
            requestId={order.id}
          />
          <Box sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider', borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
            <Typography variant="overline" display="block" sx={{ color: 'text.secondary', letterSpacing: 1.5, mb: 0.5 }}>
              AUFTRAGSAUSFÜHRUNG
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
              {order.title}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
              Anfrage von {order.company} • Eingangsdatum: {DateUtils.formatDate(order.date)}
              {order.statusHistory && order.statusHistory.in_progress && (
                <Chip 
                  size="small" 
                  label={`Bearbeitungszeitraum: ${DateUtils.formatProcessingPeriod(order.statusHistory.in_progress.startDate, order.statusHistory.in_progress.endDate)}`} 
                  color="primary" 
                  variant="outlined"
                  sx={{ ml: 2, height: 24, fontSize: '0.75rem' }}
                />
              )}
            </Typography>
          </Box>
        </Paper>

        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/incoming-requests')}
            startIcon={<ChevronLeftIcon />}
          >
            Zurück zur Übersicht
          </Button>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip 
              icon={<CheckCircleIcon fontSize="small" />}
              label={`${order.steps.filter(s => s.status === 'completed').length} Schritte abgeschlossen`} 
              color="success" 
              variant="outlined" 
              sx={{ fontWeight: 'medium' }}
            />
            <Chip 
              label={`Patienten: ${order.patientCount}`} 
              color="primary" 
              variant="outlined" 
              sx={{ fontWeight: 'medium' }}
            />
          </Box>
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
              <CardHeader 
                title="Auftragsdetails" 
                sx={{ 
                  bgcolor: 'rgba(255, 122, 0, 0.05)', 
                  borderBottom: '1px solid', 
                  borderColor: 'rgba(255, 122, 0, 0.1)',
                  py: 1.5
                }}
              />
              <CardContent sx={{ px: 3, py: 2.5 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  {order.title}
                </Typography>
                
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Unternehmen</Typography>
                    <Typography variant="body1" fontWeight="medium">{order.company}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Eingangsdatum</Typography>
                    <Typography variant="body1" fontWeight="medium">{DateUtils.formatDate(order.date)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Patientenzahl</Typography>
                    <Typography variant="body1" fontWeight="medium">{order.patientCount}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Preis</Typography>
                    <Typography variant="body1" fontWeight="medium">{order.price.toLocaleString('de-DE')} €</Typography>
                  </Grid>
                </Grid>
                
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'background.default', 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  mb: 2
                }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    Gesamtfortschritt
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={
                      order.steps.reduce((acc, step) => acc + step.progress, 0) / order.steps.length
                    } 
                    sx={{ height: 8, borderRadius: 4, mb: 1 }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Beginn: {DateUtils.formatDate(order.date)}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="primary.main">
                      {Math.round(order.steps.reduce((acc, step) => acc + step.progress, 0) / order.steps.length)}%
                    </Typography>
                  </Box>
                </Box>
                
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Beschreibung
                </Typography>
                <Typography variant="body2" paragraph sx={{ color: 'text.secondary' }}>
                  {order.description}
                </Typography>
                
                <Button 
                  variant="outlined" 
                  fullWidth 
                  sx={{ mt: 1 }}
                  onClick={() => window.open(`/api/requests/${order.id}/report`, '_blank')}
                >
                  Projektdokumentation anzeigen
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper elevation={2} sx={{ p: 0, borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'primary.50', 
                borderBottom: '1px solid', 
                borderColor: 'primary.100',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="h6" fontWeight="bold">
                  Prozessschritte
                </Typography>
                <Chip 
                  label={`${order.steps.filter(s => s.status === 'completed').length} von ${order.steps.length} abgeschlossen`}
                  color="primary"
                  size="small"
                  sx={{ fontWeight: 'medium' }}
                />
              </Box>
              
              <Box sx={{ p: 3 }}>
                <Stepper activeStep={activeStep} orientation="vertical">
                  {order.steps.map((step, index) => (
                    <Step key={step.id}>
                      <StepLabel
                        StepIconComponent={() => getStepIcon(step.status)}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" fontWeight="medium">{step.name}</Typography>
                          <Chip 
                            label={step.status === 'completed' ? 'Abgeschlossen' : 
                                  step.status === 'in_progress' ? 'In Bearbeitung' : 
                                  step.status === 'error' ? 'Fehler' : 'Ausstehend'} 
                            color={step.status === 'completed' ? 'success' : 
                                  step.status === 'in_progress' ? 'warning' : 
                                  step.status === 'error' ? 'error' : 'default'} 
                            size="small"
                            sx={{ fontWeight: 'medium' }}
                          />
                        </Box>
                      </StepLabel>
                      <StepContent>
                        <Box sx={{ 
                          p: 2, 
                          bgcolor: 'background.default', 
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          mb: 2,
                          mt: 1
                        }}>
                          <Typography variant="body2" paragraph>
                            {step.details}
                          </Typography>
                          {step.status === 'in_progress' && (
                            <Box sx={{ mt: 1, mb: 2 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={step.progress} 
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Begonnen: {formatDate(step.startTime)}
                                </Typography>
                                <Typography variant="body2" fontWeight="bold" color="warning.main">
                                  {step.progress}%
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          {step.status === 'completed' && (
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Start: {formatDate(step.startTime)}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Ende: {formatDate(step.endTime)}
                                </Typography>
                              </Grid>
                            </Grid>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {getStepActions(step)}
                            <Tooltip title="Details anzeigen">
                              <IconButton size="small" sx={{ bgcolor: 'background.default' }}>
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          {step.status === 'completed' && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<DownloadIcon />}
                              sx={{ textTransform: 'none' }}
                            >
                              Ergebnis herunterladen
                            </Button>
                          )}
                        </Box>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
                {activeStep === order.steps.length && (
                  <Alert 
                    severity="success" 
                    sx={{ 
                      mt: 3,
                      borderLeft: '4px solid',
                      borderColor: 'success.main'
                    }}
                    icon={<CheckCircleIcon fontSize="large" />}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      Alle Prozessschritte abgeschlossen
                    </Typography>
                    <Typography variant="body2">
                      Die Ergebnisse können nun übermittelt werden. Klicken Sie auf "Zur Ergebnisübermittlung", um fortzufahren.
                    </Typography>
                    <Button 
                      variant="contained" 
                      color="success" 
                      sx={{ mt: 1 }}
                      onClick={() => navigate(`/results-delivery?id=${order.id}`)}
                    >
                      Zur Ergebnisübermittlung
                    </Button>
                  </Alert>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </MainLayout>
  );
};

export default OrderExecution;
